import { UiService } from 'src/app/services/ui.service'

// Toast-tester variants, ordered visual-first (layout sanity) then handler-specific
// (real backend payloads). Verbiage in the passthrough group is copied verbatim from
// arpio-api UnprocessableEntity raises so the preview matches production.
export function buildToastGroups(uiService: UiService): ReadonlyArray<{
  label: string
  variants: ReadonlyArray<{ label: string; fire: () => void }>
}> {
  return [
    {
      label: 'Layout & wrapping',
      variants: [
        {
          label: 'Short message',
          fire: () =>
            uiService.announceError({
              message: 'Something went wrong',
              detailedMessage: 'Please try again.',
            }),
        },
        {
          label: 'Long single-line (width clamp)',
          fire: () =>
            uiService.announceError({
              message: 'Failed to update resource',
              detailedMessage:
                'A long detailedMessage with no explicit line breaks so we can verify the toast wraps cleanly inside the fixed-width container without bleeding past the right edge of the viewport.',
            }),
        },
        {
          label: 'Multi-line (preserves \\n)',
          fire: () =>
            uiService.announceError({
              message: 'Multi-step failure',
              detailedMessage: 'Step one failed.\nStep two failed.\n\nStep three was skipped.',
            }),
        },
      ],
    },
    {
      label: 'Plain API codes (no body)',
      variants: [
        {
          label: 'Connection error (0)',
          fire: () => uiService.announceApiError({ responseCode: 0 } as any),
        },
        {
          label: 'Invalid request (400)',
          fire: () => uiService.announceApiError({ responseCode: 400 } as any),
        },
        {
          label: 'Authentication required (401)',
          fire: () => uiService.announceApiError({ responseCode: 401 } as any),
        },
        {
          label: 'Access denied (403)',
          fire: () => uiService.announceApiError({ responseCode: 403 } as any),
        },
        {
          label: 'Not found (404)',
          fire: () => uiService.announceApiError({ responseCode: 404 } as any),
        },
        {
          label: 'Conflict (409)',
          fire: () => uiService.announceApiError({ responseCode: 409 } as any),
        },
        {
          label: 'Invalid input (422)',
          fire: () => uiService.announceApiError({ responseCode: 422 } as any),
        },
        {
          label: 'Server error (500)',
          fire: () => uiService.announceApiError({ responseCode: 500 } as any),
        },
        {
          label: 'Unrecognized code (999)',
          fire: () => uiService.announceApiError({ responseCode: 999 } as any),
        },
      ],
    },
    {
      label: 'Backend message passthrough (4xx with body)',
      variants: [
        {
          label: 'Multi-app launch failure (422)',
          fire: () =>
            uiService.announceApiError({
              responseCode: 422,
              body: {
                message:
                  'Application app-prod-web cannot be restored because of the following issues:\n' +
                  '  The applied recovery point cannot be changed because some applications are already in a test or failed over state.\n\n' +
                  'Application app-prod-api cannot be restored because of the following issues:\n' +
                  '  The applied recovery point cannot be changed because some applications are already in a test or failed over state.',
              },
            } as any),
        },
        {
          label: 'Single-app launch failure with issues list (422)',
          fire: () =>
            uiService.announceApiError({
              responseCode: 422,
              body: {
                message:
                  'Application app-prod-web cannot be restored because of the following issues:\n' +
                  '  The applied recovery point cannot be changed because some applications are already in a test or failed over state.',
              },
            } as any),
        },
        {
          label: 'Single-app launch failure, invalid request (422)',
          fire: () =>
            uiService.announceApiError({
              responseCode: 422,
              body: { message: 'Application app-prod-web cannot be restored because the request was invalid' },
            } as any),
        },
        {
          label: 'Restore disabled for app (422)',
          fire: () =>
            uiService.announceApiError({
              responseCode: 422,
              body: { message: 'Restore is disabled for application app-prod-web' },
            } as any),
        },
        {
          label: 'No applications to restore (422)',
          fire: () =>
            uiService.announceApiError({
              responseCode: 422,
              body: { message: 'No applications to restore.' },
            } as any),
        },
        {
          label: 'Account not entitled (422)',
          fire: () =>
            uiService.announceApiError({
              responseCode: 422,
              body: { message: 'This Arpio account is not entitled to restore applications.' },
            } as any),
        },
        {
          label: 'Retry only completed jobs (422)',
          fire: () =>
            uiService.announceApiError({
              responseCode: 422,
              body: { message: 'Only completed jobs can be retried. Job status: IN_PROGRESS' },
            } as any),
        },
        {
          label: 'Resume recovery unavailable (422)',
          fire: () =>
            uiService.announceApiError({
              responseCode: 422,
              body: {
                message:
                  'Resume recovery is not available for this application. Cause: RECOVERY_POINT_NO_LONGER_AVAILABLE',
              },
            } as any),
        },
        {
          label: 'RP lock acquisition failed (422)',
          fire: () =>
            uiService.announceApiError({
              responseCode: 422,
              body: {
                message:
                  'Restore request failed because of the following issue: A different restore is already in flight for this recovery point.',
              },
            } as any),
        },
        {
          label: 'Generic backend detail (400)',
          fire: () =>
            uiService.announceApiError({
              responseCode: 400,
              body: { message: 'Required field "applicationId" is missing from the request body.' },
            } as any),
        },
      ],
    },
    {
      label: 'Specialized substring-matched handlers',
      variants: [
        {
          label: 'Duplicate role name (422)',
          fire: () =>
            uiService.announceApiError({
              responseCode: 422,
              body: { message: 'Role name already exists.' },
            } as any),
        },
        {
          label: 'SAML metadata expired (422)',
          fire: () =>
            uiService.announceApiError({
              responseCode: 422,
              body: { message: 'SAML metadata has expired.' },
            } as any),
        },
        {
          label: 'SAML metadata missing EntityDescriptor (422)',
          fire: () =>
            uiService.announceApiError({
              responseCode: 422,
              body: { message: 'SAML metadata is missing the entityDescriptor element.' },
            } as any),
        },
        {
          label: 'Email verification required (403)',
          fire: () =>
            uiService.announceApiError({
              responseCode: 403,
              body: { message: 'You must verify your email address before adding users.' },
            } as any),
        },
        {
          label: 'Email cooldown 90s (403)',
          fire: () =>
            uiService.announceApiError({
              responseCode: 403,
              body: { message: 'You must wait at least 90 seconds since the last email was sent.' },
            } as any),
        },
      ],
    },
    {
      label: 'Real-world examples',
      variants: [
        {
          label: 'Restore failed, recovery point missing',
          fire: () =>
            uiService.announceError({
              message: 'Recovery Failed',
              detailedMessage:
                'The recovery point you selected is no longer available. It may have been deleted or pruned by your retention policy. Refresh and try a different recovery point.',
            }),
        },
        {
          label: 'HubSpot widget unavailable',
          fire: () =>
            uiService.announceError({
              message: 'Unable to find HubSpot chat widget.',
              detailedMessage: 'Unable to find HubSpot chat widget.',
            }),
        },
      ],
    },
  ]
}
